import useCustomerAction from '../hooks/useCustomerAction';
import SellerShopCard from '../components/SellerShopCard';
import ProductImage from '../components/ProductImage';
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { productApi } from '../services/productApi';
import { useCart } from '../context/CartContext';
import { useFavorites } from '../context/FavoriteContext';
import { getImageUrls } from '../utils/image';
import {
  normalizeProduct,
  getRetailPrice,
  getDiscount,
  getAvailableVariants,
} from '../services/productService';

function ProductPageMobile() {
  const { id } = useParams();
  const authorize = useCustomerAction();
  const [purchaseNotice, setPurchaseNotice] = useState('');
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [bundleItems, setBundleItems] = useState([]);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [currentImg, setCurrentImg] = useState(0);
  const [reviews, setReviews] = useState([]);
  const [avgRating, setAvgRating] = useState(0);
  const [reviewsCount, setReviewsCount] = useState(0);
  const [questionsCount, setQuestionsCount] = useState(0);
  const [showDetails, setShowDetails] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isBarTransparent, setIsBarTransparent] = useState(true);
  const { dispatch: cartDispatch } = useCart();
  const { isFavorite, dispatch: favDispatch } = useFavorites();
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const isSwiping = useRef(false);
  const imgContainerRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  useEffect(() => {
    productApi
      .getById(id)
      .then((data) => {
        const normalized = normalizeProduct(data);
        setProduct(normalized);
        const available = getAvailableVariants(normalized.variants)[0] || null;
        if (available) setSelectedVariant(available);
        const gid = normalized.groupId;
        if (gid) {
          productApi
            .getGroup(gid)
            .then((res) => {
              const list = Array.isArray(res) ? res : res.products || [];
              setBundleItems(list.filter((p) => p.id !== parseInt(id)));
            })
            .catch(() => setBundleItems([]));
        }
        productApi
          .getReviews(id)
          .then((r) => {
            if (r?.length) {
              setReviews(r.slice(0, 3));
              setReviewsCount(r.length);
              setAvgRating(
                (
                  r.reduce((sum, rev) => sum + rev.rating, 0) / r.length
                ).toFixed(1)
              );
            }
          })
          .catch(() => {});
        productApi
          .getQuestions(id)
          .then((q) => {
            if (q?.length) setQuestionsCount(q.length);
          })
          .catch(() => {});
      })
      .catch(() => setProduct(null));
  }, [id]);

  useEffect(() => {
    const handleScroll = () => {
      setIsBarTransparent(window.scrollY < 1);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const fullImageUrls = getImageUrls(product).length ? getImageUrls(product) : [null];

  const handleImgTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
    isSwiping.current = false;
    setDragOffset(0);
    setIsDragging(true);
  };

  const handleImgTouchMove = (e) => {
    if (!isDragging) return;
    const deltaX = e.touches[0].clientX - touchStartX.current;
    const deltaY = e.touches[0].clientY - touchStartY.current;
    if (Math.abs(deltaX) > Math.abs(deltaY)) {
      e.preventDefault();
      isSwiping.current = true;
      const w = imgContainerRef.current?.offsetWidth || 1;
      setDragOffset(Math.max(-w * 0.5, Math.min(w * 0.5, deltaX)));
    } else {
      setIsDragging(false);
      setDragOffset(0);
    }
  };

  const handleImgTouchEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);
    const threshold = (imgContainerRef.current?.offsetWidth || 300) * 0.3;
    if (dragOffset < -threshold && currentImg < fullImageUrls.length - 1)
      setCurrentImg((prev) => prev + 1);
    else if (dragOffset > threshold && currentImg > 0)
      setCurrentImg((prev) => prev - 1);
    setDragOffset(0);
  };

  const imgTranslateX =
    -currentImg * 100 +
    (dragOffset / (imgContainerRef.current?.offsetWidth || 1)) * 100;

  const addToCart = () => {
    if (!selectedVariant || selectedVariant.stock <= 0 || getRetailPrice(product, selectedVariant) <= 0) { setPurchaseNotice('Выберите доступный размер'); return; }
    authorize({ type: 'cart', productId: product.id, variantId: selectedVariant.id }, () => {
      cartDispatch({ type: 'ADD_TO_CART', payload: { ...product, selectedVariantId: selectedVariant.id, selectedSize: selectedVariant.size, price: getRetailPrice(product, selectedVariant), stock: selectedVariant.stock } });
      setPurchaseNotice('Товар добавлен в корзину');
    });
  };
  const buyNow = () => {
    if (!selectedVariant || selectedVariant.stock <= 0 || getRetailPrice(product, selectedVariant) <= 0) { setPurchaseNotice('Выберите доступный размер'); return; }
    authorize({ type: 'buy-now', productId: product.id, variantId: selectedVariant.id }, () => {
      cartDispatch({ type: 'ADD_TO_CART', payload: { ...product, selectedVariantId: selectedVariant.id, selectedSize: selectedVariant.size, price: getRetailPrice(product, selectedVariant), stock: selectedVariant.stock } });
      cartDispatch({ type: 'SELECT_ONLY', payload: selectedVariant.id });
      navigate('/checkout');
    });
  };
  const toggleFavorite = () => authorize({ type: 'favorite', productId: product.id }, () => favDispatch({ type: 'TOGGLE_FAVORITE', payload: product }));
  const shareProduct = () => {
    const url = window.location.href;
    if (navigator.share)
      navigator.share({ title: product.title, url }).catch(() => {});
    else {
      navigator.clipboard.writeText(url);
      alert('Ссылка скопирована');
    }
  };

  const handleBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/');
  };

  if (!product)
    return (
      <div style={{ textAlign: 'center', padding: '2rem' }}>Загрузка...</div>
    );

  const displayPrice =
    selectedVariant?.retailPrice ?? getRetailPrice(product, selectedVariant);
  const oldPrice = product.oldPrice;
  const discountPercent =
    selectedVariant?.discountPercent ?? getDiscount(product, displayPrice);
  const favorite = isFavorite(product.id);

  // Клиентская логика: разделяем размеры на доступные и недоступные
  const sortedVariants = product.variants || [];
  const availableVariants = sortedVariants.filter((v) => v.stock > 0);
  const unavailableVariants = sortedVariants.filter((v) => v.stock === 0);
  const allVariants = [...availableVariants, ...unavailableVariants];

  return (
    <div
      style={{
        background: '#fff',
        minHeight: '100vh',
        paddingBottom: 130,
        margin: 0,
      }}
    >
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: 50,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '0 12px',
          background: isBarTransparent ? 'transparent' : 'white',
          backdropFilter: isBarTransparent ? 'none' : 'blur(0)',
          zIndex: 1000,
          transition: 'background 0.3s',
          borderBottom: isBarTransparent ? 'none' : '1px solid #f0f0f0',
        }}
      >
        <button
          onClick={handleBack}
          style={{
            background: 'rgba(255,255,255,0.5)',
            border: 'none',
            borderRadius: '50%',
            width: 36,
            height: 36,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 20,
            cursor: 'pointer',
            color: '#1e293b',
          }}
        >
          ←
        </button>
        <div style={{ display: 'flex', gap: 12 }}>
          <button
            onClick={toggleFavorite}
            style={{
              background: 'rgba(255,255,255,0.5)',
              border: 'none',
              borderRadius: '50%',
              width: 36,
              height: 36,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              padding: 0,
            }}
          >
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill={favorite ? '#EF4444' : 'rgba(255,255,255,0.4)'}
              stroke="currentColor"
              strokeWidth="2"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
              />
            </svg>
          </button>
          <button
            onClick={shareProduct}
            style={{
              background: 'rgba(255,255,255,0.5)',
              border: 'none',
              borderRadius: '50%',
              width: 36,
              height: 36,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8M16 6l-4-4-4 4M12 2v13" />
            </svg>
          </button>
        </div>
      </div>

      <div
        ref={imgContainerRef}
        style={{
          position: 'relative',
          width: '100%',
          aspectRatio: '3/4',
          overflow: 'hidden',
          background: '#f5f5f5',
        }}
        onTouchStart={handleImgTouchStart}
        onTouchMove={handleImgTouchMove}
        onTouchEnd={handleImgTouchEnd}
      >
        <div
          style={{
            display: 'flex',
            height: '100%',
            transform: `translateX(${imgTranslateX}%)`,
            transition: isDragging ? 'none' : 'transform 0.3s ease',
            willChange: 'transform',
            touchAction: 'pan-y pinch-zoom',
          }}
        >
          {fullImageUrls.map((url, idx) => (
            <ProductImage key={idx} product={product} src={url} alt={`${product.title} ${idx + 1}`} width="100%" height="100%" style={{ flexShrink: 0 }} loading="eager" draggable={false} />
          ))}
        </div>
        {fullImageUrls.length > 1 && (
          <div
            style={{
              position: 'absolute',
              bottom: 12,
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              gap: 6,
            }}
          >
            {fullImageUrls.map((_, idx) => (
              <div
                key={idx}
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background:
                    idx === currentImg ? 'white' : 'rgba(255,255,255,0.5)',
                }}
              />
            ))}
          </div>
        )}
      </div>

      <div style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
          <span style={{ fontSize: 24, fontWeight: 700, color: '#7C3AED' }}>
            {displayPrice} ₽
          </span>
          {oldPrice && (
            <span
              style={{
                textDecoration: 'line-through',
                color: '#94A3B8',
                fontSize: 16,
              }}
            >
              {oldPrice} ₽
            </span>
          )}
          {discountPercent > 0 && (
            <span
              style={{
                background: '#EF4444',
                color: 'white',
                padding: '2px 6px',
                borderRadius: 10,
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              -{discountPercent}%
            </span>
          )}
        </div>
      </div>

      {(purchaseNotice || authorize.error) && <p className="purchase-notice" role="status">{authorize.error || purchaseNotice}</p>}
      {bundleItems.length > 0 && (
        <div className="bundle-colors-inline">
          {bundleItems.map((b) => {

            return (
              <Link
                to={`/product/${b.id}`}
                key={b.id}
                replace
                className="bundle-color-square"
                title={b.color || b.title}
              >
                <ProductImage product={b} alt={b.color || b.title} width="100%" height="100%" />
              </Link>
             );
          })}
        </div>
      )}

      {product.color && (
        <div
          style={{
            padding: '8px 16px',
            borderBottom: '1px solid #f0f0f0',
            fontSize: 15,
            color: '#475569',
          }}
        >
          Цвет: <strong>{product.color}</strong>
        </div>
      )}

      <div style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0' }}>
        <div style={{ fontWeight: 600, marginBottom: 8, color: '#1e293b' }}>
          Размер
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {allVariants.map((v) => {
            const isAvail = v.stock > 0;
            const isSel = selectedVariant?.id === v.id;
            return (
              <button
                key={v.id}
                onClick={() => {
                  if (isAvail) setSelectedVariant(v);
                }}
                disabled={!isAvail}
                style={{
                  padding: '8px 14px',
                  borderRadius: 8,
                  border: isSel ? '2px solid #7C3AED' : '1px solid #e2e8f0',
                  background: isSel ? '#7C3AED' : 'white',
                  color: isSel ? 'white' : isAvail ? '#1e293b' : '#cbd5e1',
                  fontWeight: 500,
                  cursor: isAvail ? 'pointer' : 'default',
                  opacity: isAvail ? 1 : 0.5,
                }}
              >
                <span>{v.size}</span>
                {/* Надпись "Нет" удалена */}
              </button>
            );
          })}
        </div>
      </div>

      <div
        style={{
          padding: '12px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: 24,
          borderBottom: '1px solid #f0f0f0',
        }}
      >
        <div
          onClick={() => navigate(`/product/${id}/reviews`)}
          style={{
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          <span style={{ color: '#F59E0B', fontSize: 16 }}>★</span> {avgRating}{' '}
          ({reviewsCount})
        </div>
        <div
          onClick={() => navigate(`/product/${id}/questions`)}
          style={{ cursor: 'pointer', color: '#64748b' }}
        >
          {questionsCount} вопросов
        </div>
      </div>

      {reviews.length > 0 && (
        <div style={{ padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
          <div
            style={{
              display: 'flex',
              overflowX: 'auto',
              scrollSnapType: 'x mandatory',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              WebkitOverflowScrolling: 'touch',
            }}
          >
            {reviews.map((rev) => (
              <div
                key={rev.id}
                style={{
                  minWidth: '100vw',
                  padding: '0 16px',
                  boxSizing: 'border-box',
                  scrollSnapAlign: 'start',
                }}
              >
                <div
                  style={{
                    background: '#f9fafb',
                    borderRadius: 12,
                    padding: 16,
                  }}
                >
                  <div style={{ color: '#F59E0B', marginBottom: 6 }}>
                    {'★'.repeat(rev.rating)}
                  </div>
                  <div
                    style={{ fontSize: 15, color: '#1e293b', lineHeight: 1.5 }}
                  >
                    {rev.text}
                  </div>
                  <div style={{ fontSize: 13, color: '#94a3b8', marginTop: 8 }}>
                    {rev.customer_name}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              gap: 6,
              marginTop: 8,
            }}
          >
            {reviews.map((_, idx) => (
              <div
                key={idx}
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: idx === currentReviewIdx ? '#7C3AED' : '#e2e8f0',
                }}
              />
            ))}
          </div>
        </div>
      )}

      <div style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0' }}>
        <div
          onClick={() => setShowDetails(!showDetails)}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            cursor: 'pointer',
          }}
        >
          <div>
            <h2
              style={{
                fontSize: 17,
                fontWeight: 600,
                color: '#1e293b',
                margin: 0,
              }}
            >
              {product.title}
            </h2>
            <div style={{ fontSize: 14, color: '#64748b' }}>
              Арт. {product.platformSku || product.platform_sku}
            </div>
          </div>
          <span style={{ fontSize: 18, color: '#7C3AED' }}>
            {showDetails ? '▲' : '▼'}
          </span>
        </div>
        {showDetails && (
          <div style={{ marginTop: 10, fontSize: 14, color: '#475569' }}>
            <p>{product.description}</p>
            {product.brand && <p>Бренд: {product.brand}</p>}
            <p>
              Категория: {product.category}
              {product.subcategory ? ` / ${product.subcategory}` : ''}
            </p>
          </div>
        )}
      </div>

      <div className="seller-shop-mobile">
        <SellerShopCard sellerId={product.sellerId || product.seller_id} fallbackName={product.seller?.name || product.sellerName} />
      </div>

      <div
        style={{
          position: 'fixed',
          bottom: 50,
          left: 0,
          right: 0,
          background: 'white',
          borderTop: '1px solid #e2e8f0',
          padding: '12px 16px',
          display: 'flex',
          gap: 10,
          zIndex: 1200,
          boxShadow: '0 -2px 10px rgba(0,0,0,0.05)',
        }}
      >
        <button
          onClick={buyNow}
          style={{
            flex: 1,
            padding: '14px',
            background: '#7C3AED',
            color: 'white',
            border: 'none',
            borderRadius: 8,
            fontSize: 16,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Купить сейчас
        </button>
        <button
          onClick={addToCart}
          style={{
            flex: 1,
            padding: '14px',
            background: 'white',
            color: '#7C3AED',
            border: '1px solid #7C3AED',
            borderRadius: 8,
            fontSize: 16,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          В корзину
        </button>
      </div>
    </div>
  );
}

export default ProductPageMobile;
